import { createBaseButtonProps } from "../models/BaseDeviceProps";
import * as React from "react";
import Select from "react-select";
import { TemplateFrontImage } from "./ProductSelection/TemplateFrontImage";
import { Field } from "../modelDialogs/BaseDialog";
import { EditorField } from "./EditorField";
import type Device from "../models/Device";
import { useOnChangeWatcher } from "../models/useOnChangeWatcher";
import CatalogProductDialog from "./ProductSelection/CatalogProductDialog";
import { importCatalogProduct } from "./ProductSelection/importCatalogProduct";
import type { CatalogProductType } from "./ProductSelection/CatalogProductPicker";
import { getTemplateGroup } from "../models/TemplateGroup";
import { itx } from "../translationItx/itx";
import { templateSelectionOptions, selectedTemplateOption, visibleTemplateOptions, type TemplateSelectionOption } from "./ProductSelection/templateSelectionOptions";

export interface TemplateSelectionProps {
  device: Device;
  productType: Exclude<CatalogProductType, "All">;
  /** Rendering location: General tab or compact C1 docking editor, independent of application mode. */
  context:
    | "main" // no itx
    | "c1"; // no itx
  withoutLable?: boolean;
  layout?:
    | "table-row" // no itx
    | "standalone"; // no itx
  /** Extremely narrow hosts (e.g. the floating channel popup) where the control must never wrap its arrow below the value. */
  compact?: boolean;
}

export function TemplateSelection(props: TemplateSelectionProps) {
  const { device, productType, context, withoutLable, layout = "standalone", compact = false } = props;
  // The menu is portalled to document.body and cannot inherit the field's font size.
  const productSelectionFontSize = compact
    ? "11px" // no itx
    : context === "main"
      ? "var(--font-standard, 16px)" // no itx
      : "13px"; // no itx

  const [collapsedGroups, setCollapsedGroups] = React.useState<Set<string>>(() => new Set());
  const [menuOpen, setMenuOpen] = React.useState(false);
  const [contentWidth, setContentWidth] = React.useState(0);
  React.useLayoutEffect(() => {
    if (!menuOpen) return;
    let requiredWidth = 0;
    for (const label of document.querySelectorAll<HTMLElement>(".templateSelectionMenuLabel")) {
      if (label.dataset.deviceId !== device.id) continue;
      // Measure the unwrapped content using its real font, images, arrow and indentation.
      const probe = label.cloneNode(true) as HTMLElement;
      Object.assign(probe.style, {
        position: "fixed",
        visibility: "hidden",
        pointerEvents: "none",
        left: "0",
        top: "0",
        width: "max-content",
        maxWidth: "none",
        whiteSpace: "nowrap",
        font: getComputedStyle(label).font,
      });
      probe.removeAttribute("class"); // no itx
      document.body.appendChild(probe);
      requiredWidth = Math.max(requiredWidth, probe.getBoundingClientRect().width);
      probe.remove();
    }
    // Option padding plus room for the scrollbar.
    setContentWidth(Math.ceil(requiredWidth) + 40);
  });
  useOnChangeWatcher(device);

  const aktorButtonTemplateGroup = getTemplateGroup(productType);

  useOnChangeWatcher(aktorButtonTemplateGroup);

  async function applyTemplate(template: { id: string }) {
    await device.setAndLoadTemplate(template.id);
    await device.updateAllFromKnxMetadata();
    device.raiseOnChange("template");
    device.knxInstanceData.raiseOnChange();
    device.template?.raiseOnChange();
  }

  async function loadNewTemplateFromServer() {
    if (aktorButtonTemplateGroup) {
      if (aktorButtonTemplateGroup.templateGroups.size > 0) {
        const { openTemplateGroupProductSelection } = await import(
          "./ProductSelection/openTemplateGroupProductSelection"
        );
        await openTemplateGroupProductSelection(aktorButtonTemplateGroup, applyTemplate);
        return;
      }
      await state.dialogManager.openDialog(
        <CatalogProductDialog
          title={itx("productSelection.selectFor", "Produkt auswählen für: {{name}}", {
            "{{name}}": device.displayName,
          })}
          productType={productType}
          genericDefaults={{
            buttonBaseProps: createBaseButtonProps({
              numberOfButtonRows: device.baseProps.buttonBaseProps?.numberOfButtonRows ?? 1,
            }),
            actorBaseProps: { channels: Math.max(1, device.channels.size), deviceType: device.deviceType },
          }}
          onSelect={async (product) => {
            const template = await importCatalogProduct(aktorButtonTemplateGroup, product);
            await applyTemplate(template);
          }}
        />,
        true,
      );
    }
  }

  const options = templateSelectionOptions(aktorButtonTemplateGroup, device.template, productType === "Button");
  const selectedValue = selectedTemplateOption(options, device.templateId);
  const visibleOptions = visibleTemplateOptions(options, collapsedGroups);
  const lastInLevel = new Set(
    visibleOptions
      .filter((option, index) => option.depth > (visibleOptions[index + 1]?.depth ?? 0))
      .map((option) => option.value),
  );

  async function selectTemplate(value: string) {
    if (value === "load") {
      await loadNewTemplateFromServer();
    } else {
      const template = options.find((option) => option.value === value)?.template;
      if (template && template.id !== device.templateId) await applyTemplate(template);
    }
  }

  const control = (
    <div
      className="estadaRow left w100"
      style={{
        paddingRight: compact ? 4 : context === "c1" ? 0 : "var(--template-selection-end-padding, 10px)",
        boxSizing: "border-box",
        minWidth: 0,
      }}
    >
      <Select<TemplateSelectionOption, false>
        classNamePrefix="estada-editorSelect"
        inputId={`templateSelection-${device.id}`}
        aria-label={state.translate("components.product", "product")}
        value={options.find((option) => option.value === selectedValue) ?? null}
        placeholder={state.translate("tabGeneralDevice.selectTemplate", "Wähle eine Vorlage")}
        options={[
          ...visibleOptions,
          {
            value: "load", // no itx
            depth: 0,
            label: state.translate("tabGeneralDevice.loadNewTemplate", "lade ein neues Produkt hoch"),
          },
        ]}
        isOptionDisabled={(option) => option.value !== "load" && !option.template}
        isSearchable={false}
        onMenuOpen={() => {
          setCollapsedGroups(new Set(options.filter((option) => option.expandable).map((option) => option.value)));
          setMenuOpen(true);
        }}
        onMenuClose={() => setMenuOpen(false)}
        formatOptionLabel={(option, meta) => {
          const label = option.label
            .split("|")
            .map((part) => part.trim())
            .join(", ");
          return meta.context === "menu" ? (
            <span
              className="templateSelectionMenuLabel"
              data-device-id={device.id}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                whiteSpace: "normal",
                overflowWrap: "anywhere",
                paddingLeft: `${option.depth * 42}px`,
              }}
            >
              {option.expandable && (
                <button
                  type="button"
                  aria-label={`${collapsedGroups.has(option.value) ? itx("expand", "Aufklappen") : itx("collapse", "Zuklappen")}: ${label}`}
                  aria-expanded={!collapsedGroups.has(option.value)}
                  style={{
                    border: 0,
                    background: "transparent",
                    color: "inherit",
                    width: 32,
                    minWidth: 32,
                    margin: 0,
                    boxSizing: "border-box",
                    padding: "4px",
                    cursor: "pointer",
                    flexShrink: 0,
                  }}
                  onMouseDown={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") event.stopPropagation();
                  }}
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    setCollapsedGroups((previous) => {
                      const next = new Set(previous);
                      if (next.has(option.value)) next.delete(option.value);
                      else next.add(option.value);
                      return next;
                    });
                  }}
                >
                  <i
                    aria-hidden="true"
                    className={`las la-angle-${collapsedGroups.has(option.value) ? "right" : "down"}`}
                  />
                </button>
              )}
              {option.template && <TemplateFrontImage key={option.template.id} template={option.template} />}
              <span style={{ minWidth: 0 }}>{label}</span>
            </span>
          ) : (
            <span
              style={{
                display: "block",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                paddingLeft: 0,
              }}
            >
              {option.label.split("|", 1)[0].trim()}
            </span>
          );
        }}
        menuPortalTarget={document.body}
        menuPosition="fixed"
        styles={{
          container: (base) => ({
            ...base,
            flex: "1 1 0",
            width: 0,
            minWidth: 0,
            maxWidth: "100%",
            fontSize: compact ? productSelectionFontSize : context === "c1" ? "inherit" : productSelectionFontSize,
          }),
          // In very narrow hosts the default control wraps the dropdown arrow below the value; keep both on one line and shrink its hit area instead.
          control: (base) => (compact ? { ...base, flexWrap: "nowrap", minHeight: 22, minWidth: 46 } : base),
          valueContainer: (base) => (compact ? { ...base, flexWrap: "nowrap", padding: "0 4px" } : base),
          indicatorsContainer: (base) => (compact ? { ...base, flexShrink: 0 } : base),
          dropdownIndicator: (base) => (compact ? { ...base, padding: 2 } : base),
          indicatorSeparator: (base) => (compact ? { ...base, marginTop: 4, marginBottom: 4 } : base),
          singleValue: (base) => ({
            ...base,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "clip",
            maxWidth: "100%",
          }),
          option: (base, props) => ({
            ...base,
            overflowWrap: "anywhere",
            marginBottom: lastInLevel.has(props.data.value) ? 5 : 0,
          }),
          menuPortal: (base) => {
            const viewportWidth = document.documentElement.clientWidth;
            const width = Math.min(Math.max(Number(base.width), contentWidth), Math.max(0, viewportWidth - 20));
            const left = Math.max(10, Math.min(Number(base.left), viewportWidth - width - 10));
            return { ...base, width, left, zIndex: 20000, fontSize: productSelectionFontSize };
          },
        }}
        onChange={async (option) => {
          if (!option) return;
          await selectTemplate(option.value);
        }}
      />
      {context === "main" && (
        <div
          className="hoverable hoverableText"
          style={{ marginLeft: compact ? "4px" : "10px" }}
          data-tooltip-id="tv-tooltip"
          data-tooltip-html={itx("TabGeneralDevice.navigierezumprodukt", "Navigiere zum Produkt")}
          data-tooltip-place="bottom-end" // no itx
          onClick={(ev) => {
            ev.stopPropagation();
            if (device.template) {
              state.selectionManager.selectedObject = device.template;
            }
          }}
        >
          <i className="las la-external-link-alt"></i>
        </div>
      )}
    </div>
  );
  const label = withoutLable ? "" : state.translate("components.product", "product");
  if (context === "c1")
    return (
      <EditorField label={label} htmlFor={`templateSelection-${device.id}`} className="dockLabel">
        {control}
      </EditorField>
    );
  const field = (
    <Field
      label={label}
      labelStyle={
        withoutLable
          ? undefined
          : {
              paddingRight: "var(--template-selection-label-gap, 6px)",
              width: "1%",
              whiteSpace: "nowrap",
              fontSize: productSelectionFontSize,
            }
      }
    >
      {control}
    </Field>
  );
  return layout === "table-row" ? (
    field
  ) : (
    <table style={{ width: "100%", borderSpacing: 0 }}>
      <tbody>{field}</tbody>
    </table>
  );
}
